import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { RiskBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";

const STAGE_COLORS = {
  reported: { bg: "var(--primary-soft)", color: "var(--primary)" },
  triage: { bg: "var(--primary-soft)", color: "var(--primary)" },
  investigation: { bg: "var(--warn-soft)", color: "var(--warn)" },
  impact_assessment: { bg: "var(--warn-soft)", color: "var(--warn)" },
  containment: { bg: "var(--warn-soft)", color: "var(--warn)" },
  remediation: { bg: "var(--warn-soft)", color: "var(--warn)" },
  legal_review: { bg: "var(--primary-soft)", color: "var(--primary)" },
  mgmt_approval: { bg: "var(--primary-soft)", color: "var(--primary)" },
  notification_decision: { bg: "var(--primary-soft)", color: "var(--primary)" },
  closed: { bg: "var(--success-soft)", color: "var(--success)" },
};

const RISK_COLORS = {
  low: { bg: "var(--success-soft)", color: "var(--success)" },
  medium: { bg: "var(--warn-soft)", color: "var(--warn)" },
  high: { bg: "var(--danger-soft)", color: "var(--danger)" },
  critical: { bg: "var(--danger-soft)", color: "var(--danger)" },
};

function Pill({ label, mapObj, testId }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {(label || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyForm = {
  title: "", incident_type: "unauthorized_access",
  detected_at: "", occurred_at: "",
  affected_system: "", department: "security",
  data_types_affected: [], principals_affected: 0,
  description: "", root_cause: "", impact_assessment: "",
  risk_rating: "medium", containment_actions: "", remediation_actions: "",
  assigned_team: [],
};

export default function BreachCenter() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "org_admin", "dpo", "legal_reviewer", "security_officer"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ stages: [], incident_types: [], departments: [] });
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fStage, setFStage] = useState("");
  const [fRisk, setFRisk] = useState("");
  const [fType, setFType] = useState("");
  const [fSearch, setFSearch] = useState("");

  const [newModal, setNewModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [actionForm, setActionForm] = useState({ title: "", owner: "", due_date: "", status: "open" });
  const [closeForm, setCloseForm] = useState({ root_cause: "", corrective_actions: "", lessons_learned: "", summary: "" });
  const [notifDec, setNotifDec] = useState({ notify_board: true, notify_principals: true, notify_cert_in: true, rationale: "" });
  const [hookModal, setHookModal] = useState(false);
  const [hookForm, setHookForm] = useState({ name: "", url: "", kind: "slack", events: ["incident.created", "incident.closed"], active: true });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, d, l, w] = await Promise.all([
        api.get("/breaches-meta/config"),
        api.get("/breaches-dashboard"),
        api.get("/breaches", { params: { limit: 500, stage: fStage, risk_rating: fRisk, incident_type: fType, search: fSearch } }),
        api.get("/breaches-integrations/webhooks"),
      ]);
      setMeta(m.data);
      setDash(d.data);
      setItems(l.data.items || l.data || []);
      setWebhooks(w.data.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { loadAll();   }, []);
  useEffect(() => { if (tab === "list") loadAll();   }, [fStage, fRisk, fType, fSearch]);

  const createIncident = async () => {
    try {
      const payload = {
        ...form,
        data_types_affected: typeof form.data_types_affected === "string"
          ? form.data_types_affected.split(",").map(s => s.trim()).filter(Boolean)
          : form.data_types_affected,
        assigned_team: typeof form.assigned_team === "string"
          ? form.assigned_team.split(",").map(s => s.trim()).filter(Boolean)
          : form.assigned_team,
      };
      await api.post("/breaches", payload);
      toast.success("Incident logged");
      setNewModal(false); setForm(emptyForm);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/breaches/${id}`);
      setDetail(data);
      setDetailTab("overview");
      setCloseForm({ root_cause: data.root_cause || "", corrective_actions: "", lessons_learned: "", summary: "" });
    } catch { toast.error("Failed"); }
  };

  const reloadDetail = async () => {
    if (!detail) return;
    const { data } = await api.get(`/breaches/${detail.id}`);
    setDetail(data); loadAll();
  };

  const doAction = async (path, body, msg) => {
    try {
      await api.post(`/breaches/${detail.id}/${path}`, body || {});
      toast.success(msg);
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiAnalyze = async () => {
    toast.info("Analyzing with AI...");
    try {
      const { data } = await api.post(`/breaches/${detail.id}/ai-analyze`);
      setDetail({ ...detail, ai_analysis: data });
      toast.success("AI analysis complete");
    } catch { toast.error("Failed"); }
  };

  const genNotifications = async () => {
    toast.info("Generating notifications...");
    try {
      await api.post(`/breaches/${detail.id}/generate-notifications`);
      toast.success("Draft notifications saved to Documents");
    } catch { toast.error("Failed"); }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/breaches/${detail.id}/evidence`, fd);
      toast.success("Evidence uploaded");
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const addAction = async () => {
    if (!actionForm.title) return;
    await doAction("actions", actionForm, "Action added");
    setActionForm({ title: "", owner: "", due_date: "", status: "open" });
  };

  const closeIncident = async () => {
    await doAction("close", closeForm, "Incident closed");
  };

  const decideNotify = async () => {
    await doAction("notification-decision", notifDec, "Notification decision recorded");
  };

  const saveWebhook = async () => {
    try {
      await api.post("/breaches-integrations/webhooks", hookForm);
      toast.success("Webhook saved");
      setHookModal(false);
      setHookForm({ name: "", url: "", kind: "slack", events: ["incident.created", "incident.closed"], active: true });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delWebhook = async (wid) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await api.delete(`/breaches-integrations/webhooks/${wid}`);
      toast.success("Deleted"); loadAll();
    } catch { toast.error("Failed"); }
  };

  const kanban = useMemo(() => {
    const laneKeys = meta.stages || [];
    const lanes = laneKeys.map(k => ({ key: k, label: k.replace(/_/g, " "), items: [] }));
    items.forEach(it => { const lane = lanes.find(l => l.key === it.stage); if (lane) lane.items.push(it); });
    return lanes;
  }, [items, meta.stages]);

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Breach & Incident Command Center"
        kicker="DPDP Sec. 8(6) Breach Notification"
        description="Record, investigate, contain, remediate, and notify — with AI-assisted triage and workflow enforcement."
        testId="breach-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={loadAll} data-testid="breach-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            <Btn onClick={() => { setForm(emptyForm); setNewModal(true); }} data-testid="breach-new-btn">
              <Icons.Plus className="w-3.5 h-3.5" /> Log incident
            </Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "list", label: "Incidents", icon: "AlertTriangle" },
          { key: "board", label: "Workflow board", icon: "Columns" },
          { key: "integrations", label: "Integrations", icon: "Cable" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`breach-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total incidents" value={dash.total_incidents} testId="stat-total" />
            <StatCard label="Open" value={dash.open_incidents} tone="warn" testId="stat-open" />
            <StatCard label="High risk" value={dash.high_risk_incidents} tone="danger" testId="stat-highrisk" />
            <StatCard label="Notification required" value={dash.notification_required} tone="danger" testId="stat-notif" />
            <StatCard label="Avg closure time" value={`${dash.avg_closure_hours}h`} testId="stat-closure" />
            <StatCard label="Repeated causes" value={dash.repeated_incidents?.length || 0} tone="warn" testId="stat-repeat" />
            <StatCard label="Types tracked" value={Object.keys(dash.by_type || {}).length} testId="stat-types" />
            <StatCard label="Closed" value={dash.by_stage?.closed || 0} tone="success" testId="stat-closed" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By department</div>
              <div className="space-y-1.5" data-testid="by-dept">
                {Object.entries(dash.by_department || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.Users className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{k}</div><div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By root cause</div>
              <div className="space-y-1.5" data-testid="by-cause">
                {(dash.by_root_cause || []).map(c => (
                  <div key={c.cause} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.GitBranch className="w-3 h-3" style={{ color: "var(--warn)" }} />
                    <div className="flex-1 ink truncate">{c.cause}</div><div className="muted">{c.count}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Monthly trend</div>
              <div className="space-y-1.5" data-testid="trend">
                {(dash.trend || []).slice(-12).map(t => {
                  const max = Math.max(1, ...(dash.trend || []).map(x => x.count));
                  const pct = Math.round(t.count / max * 100);
                  return (
                    <div key={t.month}>
                      <div className="flex items-center gap-2 text-[11px]"><span className="flex-1 ink mono">{t.month}</span><span className="muted">{t.count}</span></div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: "var(--primary)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Repeated incidents (same root cause)</div>
              {(dash.repeated_incidents || []).length === 0 ? (
                <div className="text-[12px] muted">No recurring root causes yet. 🎯</div>
              ) : (
                <div className="space-y-1.5" data-testid="repeated-list">
                  {dash.repeated_incidents.map(r => (
                    <div key={r.root_cause} className="p-2 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--warn)", background: "var(--warn-soft)" }}>
                      <Icons.Repeat className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />
                      <div className="flex-1 text-[11.5px] ink truncate">{r.root_cause}</div>
                      <div className="text-[10.5px] muted">×{r.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Latest activity</div>
              <div className="space-y-1.5" data-testid="activity-feed">
                {(dash.activity_feed || []).map(ev => (
                  <div key={ev.id} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                    <div className="text-[11.5px] ink"><b>{ev.action.replace(/_/g, " ")}</b> · {ev.breach_ref}</div>
                    <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {(tab === "list" || tab === "board") && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          <TextInput data-testid="filter-search" placeholder="Search title / ref / system" value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
          <SelectInput data-testid="filter-stage" value={fStage} onChange={(e) => setFStage(e.target.value)}>
            <option value="">All stages</option>
            {meta.stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </SelectInput>
          <SelectInput data-testid="filter-risk" value={fRisk} onChange={(e) => setFRisk(e.target.value)}>
            <option value="">All risks</option>
            {["low", "medium", "high", "critical"].map(r => <option key={r} value={r}>{r}</option>)}
          </SelectInput>
          <SelectInput data-testid="filter-type" value={fType} onChange={(e) => setFType(e.target.value)}>
            <option value="">All types</option>
            {meta.incident_types.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </SelectInput>
        </div>
      )}

      {tab === "list" && (
        items.length === 0 ? <EmptyState title="No incidents" hint="Log an incident to begin the workflow." /> : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["Ref", "Title", "Type", "Risk", "Stage", "Detected", "PDF", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="incidents-tbody">
                {items.map(b => (
                  <tr key={b.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`inc-row-${b.id}`}>
                    <td className="px-4 py-2 text-[11px] mono ink">{b.breach_ref}</td>
                    <td className="px-4 py-2 text-[12px] ink font-medium">{b.title}</td>
                    <td className="px-4 py-2 text-[11.5px] muted">{b.incident_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2"><Pill label={b.risk_rating} mapObj={RISK_COLORS} /></td>
                    <td className="px-4 py-2"><Pill label={b.stage} mapObj={STAGE_COLORS} /></td>
                    <td className="px-4 py-2 text-[10.5px] muted">{b.detected_at?.slice(0, 10)}</td>
                    <td className="px-4 py-2"><button onClick={() => downloadFile(`/breaches/${b.id}/report.pdf`, `breach_${b.id.slice(0, 8)}.pdf`)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`inc-pdf-${b.id}`}>PDF</button></td>
                    <td className="px-4 py-2 text-right"><button onClick={() => openDetail(b.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`inc-open-${b.id}`}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "board" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {kanban.map(lane => (
            <div key={lane.key} className="min-w-[190px] surface rounded-xl p-2" style={{ border: "1px solid var(--border)" }}>
              <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[lane.key]?.color }} />
                <div className="text-[11px] font-semibold uppercase tracking-wider ink flex-1">{lane.label}</div>
                <div className="text-[10.5px] muted">{lane.items.length}</div>
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {lane.items.map(b => (
                  <button key={b.id} onClick={() => openDetail(b.id)} data-testid={`kanban-card-${b.id}`}
                    className="w-full text-left p-2 rounded-md" style={{ background: "var(--bg-1)", border: "1px solid var(--border)" }}>
                    <div className="text-[10.5px] mono muted">{b.breach_ref}</div>
                    <div className="text-[11.5px] font-medium ink truncate">{b.title}</div>
                    <div className="mt-1"><Pill label={b.risk_rating} mapObj={RISK_COLORS} /></div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "integrations" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] font-semibold ink">Outbound webhooks</div>
              <div className="text-[11.5px] muted">Deliver incident events to Slack, Teams, SIEM, or ticketing systems.</div>
            </div>
            <Btn onClick={() => setHookModal(true)} data-testid="hook-new"><Icons.Plus className="w-3.5 h-3.5" /> Add webhook</Btn>
          </div>
          {webhooks.length === 0 ? (
            <EmptyState title="No webhooks configured" hint="Add a Slack / Teams / SIEM webhook to receive real-time alerts." />
          ) : (
            <div className="space-y-2" data-testid="webhook-list">
              {webhooks.map(w => (
                <div key={w.id} className="surface rounded-xl p-4 flex items-center gap-3" style={{ border: "1px solid var(--border)" }}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    {w.kind === "slack" ? <Icons.MessageSquare className="w-5 h-5" /> :
                     w.kind === "teams" ? <Icons.Users className="w-5 h-5" /> :
                     w.kind === "siem" ? <Icons.ShieldAlert className="w-5 h-5" /> :
                     w.kind === "ticketing" ? <Icons.Ticket className="w-5 h-5" /> : <Icons.Webhook className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium ink">{w.name} <span className="text-[10.5px] muted mono ml-2">{w.kind}</span></div>
                    <div className="text-[10.5px] muted truncate">{w.url}</div>
                    <div className="text-[10px] muted mt-0.5">Events: {(w.events || []).join(", ")}</div>
                  </div>
                  <button onClick={() => delWebhook(w.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }}>Delete</button>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 rounded-md text-[11.5px]" style={{ background: "var(--bg-2)" }}>
            <div className="font-medium ink mb-1"><Icons.Info className="inline w-3.5 h-3.5 mr-1" /> Supported events</div>
            <div className="mono muted">incident.created · incident.closed · incident.stage.{"{"}stage{"}"}</div>
          </div>
        </div>
      )}

      {/* NEW INCIDENT */}
      <Dialog open={newModal} onOpenChange={setNewModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Log a new incident</DialogTitle>
            <DialogDescription>Capture initial facts. Details can be enriched later via the workflow.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto">
            <Field label="Title" required className="col-span-2"><TextInput data-testid="i-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Incident type">
              <SelectInput data-testid="i-type" value={form.incident_type} onChange={(e) => setForm({ ...form, incident_type: e.target.value })}>
                {meta.incident_types.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Department">
              <SelectInput data-testid="i-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                {meta.departments.map(d => <option key={d} value={d}>{d}</option>)}
              </SelectInput>
            </Field>
            <Field label="Detected at"><TextInput type="datetime-local" data-testid="i-detected" value={form.detected_at?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, detected_at: e.target.value })} /></Field>
            <Field label="Occurred at"><TextInput type="datetime-local" data-testid="i-occurred" value={form.occurred_at?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} /></Field>
            <Field label="Affected system" className="col-span-2"><TextInput data-testid="i-system" value={form.affected_system} onChange={(e) => setForm({ ...form, affected_system: e.target.value })} /></Field>
            <Field label="Data types affected (comma-sep)" className="col-span-2"><TextInput data-testid="i-data" value={Array.isArray(form.data_types_affected) ? form.data_types_affected.join(", ") : form.data_types_affected} onChange={(e) => setForm({ ...form, data_types_affected: e.target.value })} /></Field>
            <Field label="Principals affected (approx)"><TextInput type="number" data-testid="i-affected" value={form.principals_affected || 0} onChange={(e) => setForm({ ...form, principals_affected: Number(e.target.value) || 0 })} /></Field>
            <Field label="Risk rating">
              <SelectInput data-testid="i-risk" value={form.risk_rating} onChange={(e) => setForm({ ...form, risk_rating: e.target.value })}>
                {["low", "medium", "high", "critical"].map(r => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Description" className="col-span-2"><TextArea data-testid="i-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Root cause (if known)"><TextInput data-testid="i-root" value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })} /></Field>
            <Field label="Assigned team (comma-sep emails)"><TextInput data-testid="i-team" value={Array.isArray(form.assigned_team) ? form.assigned_team.join(", ") : form.assigned_team} onChange={(e) => setForm({ ...form, assigned_team: e.target.value })} /></Field>
            <Field label="Impact assessment" className="col-span-2"><TextArea data-testid="i-impact" rows={2} value={form.impact_assessment} onChange={(e) => setForm({ ...form, impact_assessment: e.target.value })} /></Field>
            <Field label="Containment actions" className="col-span-2"><TextArea data-testid="i-contain" rows={2} value={form.containment_actions} onChange={(e) => setForm({ ...form, containment_actions: e.target.value })} /></Field>
            <Field label="Remediation actions" className="col-span-2"><TextArea data-testid="i-remed" rows={2} value={form.remediation_actions} onChange={(e) => setForm({ ...form, remediation_actions: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setNewModal(false)}>Cancel</Btn>
            <Btn onClick={createIncident} disabled={!form.title} data-testid="i-save">Log incident</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WEBHOOK MODAL */}
      <Dialog open={hookModal} onOpenChange={setHookModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>New webhook</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Name" required><TextInput data-testid="hook-name" value={hookForm.name} onChange={(e) => setHookForm({ ...hookForm, name: e.target.value })} /></Field>
            <Field label="Webhook URL" required><TextInput data-testid="hook-url" value={hookForm.url} onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} placeholder="https://hooks.slack.com/..." /></Field>
            <Field label="Kind">
              <SelectInput data-testid="hook-kind" value={hookForm.kind} onChange={(e) => setHookForm({ ...hookForm, kind: e.target.value })}>
                {["slack", "teams", "siem", "ticketing", "generic"].map(k => <option key={k} value={k}>{k}</option>)}
              </SelectInput>
            </Field>
            <Field label="Events (comma-sep)"><TextInput data-testid="hook-events" value={hookForm.events.join(", ")} onChange={(e) => setHookForm({ ...hookForm, events: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setHookModal(false)}>Cancel</Btn>
            <Btn onClick={saveWebhook} disabled={!hookForm.name || !hookForm.url} data-testid="hook-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL / WORKBENCH */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[13px] muted">{detail.breach_ref}</span>
                  <span>{detail.title}</span>
                  <Pill label={detail.stage} mapObj={STAGE_COLORS} testId="detail-stage" />
                  <Pill label={detail.risk_rating} mapObj={RISK_COLORS} testId="detail-risk" />
                </DialogTitle>
                <DialogDescription>
                  {(detail.incident_type || "").replace(/_/g, " ")} · {detail.affected_system || "—"} ·
                  {" "}{detail.principals_affected || 0} principals · Detected {detail.detected_at?.slice(0, 10)}
                </DialogDescription>
              </DialogHeader>

              <div className="mb-3 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
                {[
                  { key: "overview", label: "Overview", icon: "Info" },
                  { key: "workflow", label: "Workflow", icon: "GitBranch" },
                  { key: "ai", label: "AI triage", icon: "Sparkles" },
                  { key: "evidence", label: "Evidence", icon: "Paperclip" },
                  { key: "actions", label: "Actions", icon: "CheckSquare" },
                  { key: "close", label: "Close", icon: "Lock" },
                  { key: "activity", label: "Activity", icon: "Activity" },
                ].map(t => {
                  const Icon = Icons[t.icon] || Icons.Circle;
                  return (
                    <button key={t.key} data-testid={`detail-subtab-${t.key}`} onClick={() => setDetailTab(t.key)}
                      className="px-2.5 py-1 text-[11px] font-medium rounded-md flex items-center gap-1"
                      style={{ background: detailTab === t.key ? "var(--primary-soft)" : "transparent", color: detailTab === t.key ? "var(--primary)" : "var(--muted)" }}>
                      <Icon className="w-3 h-3" /> {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="max-h-[62vh] overflow-y-auto pr-1 space-y-4">
                {detailTab === "overview" && (
                  <div className="grid grid-cols-2 gap-2 text-[12px]">
                    {[
                      ["Type", detail.incident_type],
                      ["Department", detail.department],
                      ["Affected system", detail.affected_system],
                      ["Reported by", detail.reported_by],
                      ["Detected", detail.detected_at?.slice(0, 19).replace("T", " ")],
                      ["Occurred", detail.occurred_at?.slice(0, 19).replace("T", " ")],
                      ["Principals affected", detail.principals_affected],
                      ["Data types", (detail.data_types_affected || []).join(", ")],
                      ["Root cause", detail.root_cause],
                      ["Impact", detail.impact_assessment],
                      ["Containment", detail.containment_actions],
                      ["Remediation", detail.remediation_actions],
                    ].map(([k, v]) => (
                      <div key={k} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider muted">{k}</div>
                        <div className="text-[12px] ink mt-0.5">{v || "—"}</div>
                      </div>
                    ))}
                    <div className="col-span-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider muted">Description</div>
                      <div className="text-[12px] ink mt-0.5 whitespace-pre-wrap">{detail.description || "—"}</div>
                    </div>
                  </div>
                )}

                {detailTab === "workflow" && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-2">Stage transitions</div>
                      <div className="flex flex-wrap gap-2">
                        {meta.stages.map(s => (
                          <Btn key={s} size="sm" variant={detail.stage === s ? "primary" : "secondary"}
                               onClick={() => doAction("stage", { stage: s }, `Moved to ${s.replace(/_/g, " ")}`)}
                               data-testid={`stage-${s}`}>
                            {s.replace(/_/g, " ")}
                          </Btn>
                        ))}
                      </div>
                    </div>
                    {canApprove && (
                      <div className="p-3 rounded-md" style={{ border: "1px solid var(--primary)", background: "var(--primary-soft)" }}>
                        <div className="text-[11.5px] font-semibold mb-2" style={{ color: "var(--primary)" }}>Approvals</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="approvals-grid">
                          {["dpo", "legal", "security_officer", "org_admin", "management"].map(role => {
                            const a = detail.approvals?.[role];
                            return (
                              <div key={role} className="p-2 rounded-md" style={{ border: "1px solid var(--border)", background: "white" }}>
                                <div className="text-[11px] font-semibold ink">{role.replace(/_/g, " ")}</div>
                                {a ? (
                                  <>
                                    <Pill label={a.decision} mapObj={{ approve: RISK_COLORS.low, reject: RISK_COLORS.high }} />
                                    <div className="text-[9.5px] muted mt-1 truncate">{a.signed_by}</div>
                                  </>
                                ) : (
                                  <div className="flex gap-1 mt-1">
                                    <button onClick={() => doAction("approval", { role, decision: "approve", comment: "" }, "Approved")} className="text-[10px] hover:underline" style={{ color: "var(--success)" }} data-testid={`approve-${role}`}>Approve</button>
                                    <button onClick={() => doAction("approval", { role, decision: "reject", comment: "" }, "Rejected")} className="text-[10px] hover:underline" style={{ color: "var(--danger)" }} data-testid={`reject-${role}`}>Reject</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                      <div className="text-[11.5px] font-semibold mb-2">Notification decision</div>
                      <div className="flex gap-3 flex-wrap text-[11.5px]">
                        <label className="flex items-center gap-1"><input type="checkbox" data-testid="notif-board" checked={notifDec.notify_board} onChange={(e) => setNotifDec({ ...notifDec, notify_board: e.target.checked })} /> Board</label>
                        <label className="flex items-center gap-1"><input type="checkbox" data-testid="notif-principals" checked={notifDec.notify_principals} onChange={(e) => setNotifDec({ ...notifDec, notify_principals: e.target.checked })} /> Data principals</label>
                        <label className="flex items-center gap-1"><input type="checkbox" data-testid="notif-certin" checked={notifDec.notify_cert_in} onChange={(e) => setNotifDec({ ...notifDec, notify_cert_in: e.target.checked })} /> CERT-In</label>
                      </div>
                      <TextArea className="mt-2" rows={2} data-testid="notif-rationale" placeholder="Rationale for the notification decision" value={notifDec.rationale} onChange={(e) => setNotifDec({ ...notifDec, rationale: e.target.value })} />
                      <div className="mt-2 flex gap-2">
                        <Btn size="sm" onClick={decideNotify} disabled={!notifDec.rationale} data-testid="notif-save"><Icons.Save className="w-3 h-3" /> Record decision</Btn>
                        <Btn size="sm" variant="secondary" onClick={genNotifications} data-testid="notif-gen"><Icons.Sparkles className="w-3 h-3" /> Draft with AI</Btn>
                        <Btn size="sm" variant="secondary" onClick={() => doAction("notify-board", {}, "Board notified")}>Board notified</Btn>
                        <Btn size="sm" variant="secondary" onClick={() => doAction("notify-principals", {}, "Principals notified")}>Principals notified</Btn>
                        <Btn size="sm" variant="secondary" onClick={() => doAction("cert-in-report", {}, "CERT-In report generated")}>CERT-In report</Btn>
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === "ai" && (
                  <div className="space-y-3">
                    <Btn onClick={aiAnalyze} data-testid="ai-analyze"><Icons.Sparkles className="w-3.5 h-3.5" /> Run AI triage</Btn>
                    {detail.ai_analysis && (
                      <div className="p-3 rounded-md space-y-3" style={{ background: "var(--bg-2)" }} data-testid="ai-panel">
                        <div className="grid grid-cols-2 gap-2 text-[12px]">
                          <div><b>Suggested severity:</b> {detail.ai_analysis.severity}</div>
                          <div className="col-span-2"><b>Summary:</b> {detail.ai_analysis.summary}</div>
                        </div>
                        {(detail.ai_analysis.containment_steps || []).length > 0 && (
                          <div>
                            <div className="text-[11px] font-semibold ink mb-1">Suggested containment steps</div>
                            <ul className="list-disc pl-4 text-[11.5px] ink space-y-0.5">
                              {detail.ai_analysis.containment_steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {(detail.ai_analysis.missing_evidence || []).length > 0 && (
                          <div>
                            <div className="text-[11px] font-semibold ink mb-1">Missing evidence</div>
                            <ul className="list-disc pl-4 text-[11.5px] ink space-y-0.5">
                              {detail.ai_analysis.missing_evidence.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {(detail.ai_analysis.corrective_actions || []).length > 0 && (
                          <div>
                            <div className="text-[11px] font-semibold ink mb-1">Corrective actions</div>
                            <ul className="list-disc pl-4 text-[11.5px] ink space-y-0.5">
                              {detail.ai_analysis.corrective_actions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {["internal_report", "regulator_notification", "customer_notification"].map(k => detail.ai_analysis[k] && (
                          <div key={k}>
                            <div className="text-[11px] font-semibold ink mb-1">{k.replace(/_/g, " ")}</div>
                            <pre className="mono text-[10.5px] whitespace-pre-wrap p-2 rounded" style={{ background: "white", border: "1px solid var(--border)" }} data-testid={`ai-${k}`}>{detail.ai_analysis[k]}</pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {detailTab === "evidence" && (
                  <div>
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[11.5px] font-medium mb-3"
                      style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                      <Icons.Upload className="w-3.5 h-3.5" /> Upload evidence
                      <input type="file" data-testid="ev-file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadEvidence(e.target.files[0])} />
                    </label>
                    <div className="space-y-1.5" data-testid="evidence-list">
                      {(detail.attachments || []).length === 0 ? (
                        <div className="text-[12px] muted">No evidence uploaded.</div>
                      ) : detail.attachments.map(a => (
                        <button key={a.id} onClick={() => downloadFile(`/breaches/${detail.id}/evidence/${a.id}`, a.filename)}
                           className="w-full text-left p-2 rounded-md flex items-center gap-2 hover:bg-slate-50" style={{ border: "1px solid var(--border)" }}>
                          <Icons.Paperclip className="w-3.5 h-3.5" />
                          <div className="flex-1 text-[11.5px] ink truncate">{a.filename}</div>
                          <div className="text-[10.5px] muted">{Math.round(a.size / 1024)} KB · {a.uploaded_by}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {detailTab === "actions" && (
                  <div className="space-y-3">
                    <div className="p-3 rounded-md" style={{ border: "1px solid var(--primary)", background: "var(--primary-soft)" }}>
                      <div className="text-[11.5px] font-semibold mb-2" style={{ color: "var(--primary)" }}>New action</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Title" required className="col-span-2"><TextInput data-testid="act-title" value={actionForm.title} onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })} /></Field>
                        <Field label="Owner"><TextInput data-testid="act-owner" value={actionForm.owner} onChange={(e) => setActionForm({ ...actionForm, owner: e.target.value })} /></Field>
                        <Field label="Due date"><TextInput type="date" data-testid="act-due" value={actionForm.due_date} onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} /></Field>
                      </div>
                      <Btn size="sm" className="mt-2" onClick={addAction} disabled={!actionForm.title} data-testid="act-add"><Icons.Plus className="w-3 h-3" /> Add</Btn>
                    </div>
                    <div className="space-y-1.5" data-testid="actions-list">
                      {(detail.actions || []).map(a => (
                        <div key={a.id} className="p-2 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--border)" }}>
                          <Icons.CheckSquare className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                          <div className="flex-1">
                            <div className="text-[12px] ink">{a.title}</div>
                            <div className="text-[10.5px] muted">Owner: {a.owner || "—"} · Due: {a.due_date || "—"} · {a.status}</div>
                          </div>
                          <Pill label={a.status} mapObj={{ open: STAGE_COLORS.reported, done: STAGE_COLORS.closed, blocked: RISK_COLORS.high }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailTab === "close" && (
                  <div className="p-3 rounded-md" style={{ border: "1px solid var(--success)", background: "var(--success-soft)" }}>
                    <div className="text-[11.5px] font-semibold mb-2" style={{ color: "var(--success)" }}>Closure report</div>
                    <div className="grid gap-2">
                      <Field label="Root cause"><TextArea rows={2} data-testid="close-root" value={closeForm.root_cause} onChange={(e) => setCloseForm({ ...closeForm, root_cause: e.target.value })} /></Field>
                      <Field label="Corrective actions"><TextArea rows={2} data-testid="close-corr" value={closeForm.corrective_actions} onChange={(e) => setCloseForm({ ...closeForm, corrective_actions: e.target.value })} /></Field>
                      <Field label="Lessons learned"><TextArea rows={2} data-testid="close-lessons" value={closeForm.lessons_learned} onChange={(e) => setCloseForm({ ...closeForm, lessons_learned: e.target.value })} /></Field>
                      <Field label="Executive summary"><TextArea rows={3} data-testid="close-summary" value={closeForm.summary} onChange={(e) => setCloseForm({ ...closeForm, summary: e.target.value })} /></Field>
                    </div>
                    <Btn className="mt-2" onClick={closeIncident} disabled={detail.status === "closed" || !closeForm.summary} data-testid="close-btn">
                      <Icons.Lock className="w-3.5 h-3.5" /> Close incident
                    </Btn>
                  </div>
                )}

                {detailTab === "activity" && (
                  <div className="space-y-1.5" data-testid="activity-trail">
                    {(detail.activity_log || []).slice().reverse().map(ev => (
                      <div key={ev.id} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                        <div className="text-[11.5px] ink"><b>{ev.action.replace(/_/g, " ")}</b></div>
                        <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                        {ev.details && Object.keys(ev.details).length > 0 && (
                          <div className="text-[10.5px] muted mt-0.5 mono">{JSON.stringify(ev.details)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <button onClick={() => downloadFile(`/breaches/${detail.id}/report.pdf`, `breach_${detail.id.slice(0, 8)}.pdf`)}
                   className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11.5px] font-medium"
                   style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="detail-pdf">
                  <Icons.Download className="w-3.5 h-3.5" /> Download PDF report
                </button>
                <Btn variant="secondary" onClick={() => setDetail(null)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
